import { CommonHelperFunctions } from "@kuika/kuika-cl-model";
import Axios, { AxiosResponse } from "axios";
import { getRequestHeaders } from "../shared/utility/mcp-tools/helpers";

// Agent tokens (remote MCP) — the long-lived, revocable credential a headless
// client (Claude Code) presents to the AI chat server. Issued per user by
// mcp-chat-server's /api/auth/agent-token endpoints, which verify the caller
// from the same designer auth headers the bridge-token exchange uses.

const agentTokenUrl: string = `${CommonHelperFunctions.GetAiChatUrl()}/api/auth/agent-token`;

/** Metadata of an issued token. The token string itself is never listed. */
export interface AgentTokenListItem {
  tokenId: string;
  label?: string;
  createdAt: number;
  expiresAt: number;
  lastUsedAt?: number;
  revokedAt?: number;
}

export interface AgentTokenListResponse {
  tokens: AgentTokenListItem[];
}

/** The ONLY response that carries the token string — shown once, not recoverable. */
export interface IssuedAgentTokenResponse {
  agentToken: string;
  tokenId: string;
  label?: string;
  createdAt: number;
  expiresAt: number;
}

/** Thrown when the download URL does not serve a zip — e.g. a build without the plugin package, where the SPA fallback answers with index.html. */
export class PluginPackageUnavailableError extends Error {
  constructor() {
    super("The Claude Code plugin package is not available on this server.");
    this.name = "PluginPackageUnavailableError";
  }
}

/** Every zip starts with a local file header: the bytes "P", "K", 3, 4. */
const ZIP_MAGIC: readonly number[] = [0x50, 0x4b, 0x03, 0x04];

interface CreateAgentTokenBody {
  label?: string;
}

export class AgentTokenService {
  public static async List(): Promise<AxiosResponse<AgentTokenListResponse>> {
    return Axios.get<AgentTokenListResponse>(agentTokenUrl, { headers: getRequestHeaders() });
  }

  public static async Create(label: string): Promise<AxiosResponse<IssuedAgentTokenResponse>> {
    const body: CreateAgentTokenBody = { label: label.trim() || undefined };
    return Axios.post<IssuedAgentTokenResponse>(agentTokenUrl, body, { headers: getRequestHeaders() });
  }

  /**
   * Fetches the plugin zip from the designer's own static files and verifies it
   * IS a zip before anyone saves it. A missing file is not a 404 here: the SPA
   * fallback answers 200 with index.html, which a plain link would happily save
   * as "kuika-claude-plugin.zip".
   */
  public static async DownloadPlugin(url: string): Promise<Blob> {
    const response: AxiosResponse<ArrayBuffer> = await Axios.get<ArrayBuffer>(url, { responseType: "arraybuffer" });
    const head: Uint8Array = new Uint8Array(response.data.slice(0, ZIP_MAGIC.length));
    const isZip: boolean = ZIP_MAGIC.every((byte: number, index: number): boolean => head[index] === byte);
    if (!isZip) throw new PluginPackageUnavailableError();
    return new Blob([response.data], { type: "application/zip" });
  }

  public static async Revoke(tokenId: string): Promise<AxiosResponse<void>> {
    return Axios.delete<void>(`${agentTokenUrl}/${encodeURIComponent(tokenId)}`, { headers: getRequestHeaders() });
  }
}
